/*
 * Free-text search over the work order list.
 *
 * Pure, in the shape of workOrderEnums.js and materialSource.js, so the rules can be tested
 * without rendering a grid. The matching rule is shared with the register's box; see
 * features/cmms/cmmsSearch.js.
 *
 * Client-side: the list is already in the client, so the search filters something
 * we are holding. Note that the four dropdowns above it are not — status, type, priority and
 * trade go into the query key and are answered by the API. The dropdowns change what is fetched;
 * this changes what is shown.
 */
import {
  getWorkOrderPriorityLabel,
  getWorkOrderStatusLabel,
  getWorkOrderTypeLabel,
} from "../cmms/cmmsMessages";
import { filterBySearchText } from "../cmms/cmmsSearch";

/*
 * The row as it reads on screen, lower-cased.
 *
 * This is the whole difficulty of searching work orders, and the reason the register's version
 * is four lines and this one is not. Five of the eight fields are not text on the row: the asset
 * node and the assignee are ids, and status, priority and type are numbers. Searching what the
 * row literally holds would mean "line 3" and "anna" and "in progress" all find nothing while
 * the grid plainly shows them — so each is resolved the same way the grid's own column resolves
 * it, through the maps the page already builds and the label helpers in cmmsMessages.
 *
 * The maps can be empty on the first paint: the asset tree and the user list are separate
 * queries from the work orders themselves, so for a moment those two names are not in the text
 * yet. Both are cached for five minutes, so it is a first-visit flicker rather than a state
 * anyone searches in. The blank is skipped rather than written in as "undefined".
 *
 * Enum labels come back as String(value) when there is no message for them — an unmapped status
 * contributes "7". That is exactly what the badge renders, so search and grid agree even when
 * both are wrong.
 *
 * The work order id is deliberately absent. The grid hides that column by default, and folding a
 * bare number in would make a query like "12" match every order whose id merely contains it.
 */
export const getWorkOrderSearchText = (workOrder, context) => {
  const { assetNodeNameById, userNameById, intl } = context ?? {};

  return [
    workOrder?.title,
    workOrder?.description,
    assetNodeNameById?.get(workOrder?.assetNodeId),
    workOrder?.assignedResourceId ? userNameById?.get(workOrder.assignedResourceId) : null,
    workOrder?.tradeName,
    getWorkOrderStatusLabel(workOrder?.status, intl),
    getWorkOrderPriorityLabel(workOrder?.priority, intl),
    getWorkOrderTypeLabel(workOrder?.workOrderType, intl),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

/*
 * The orders matching every word of the query, in any field and any order — so "anna pump" finds
 * the pump order Anna is on, without either word having to be in the same field as the other.
 *
 * `context` carries the two lookup maps and the intl instance the resolution needs. It is passed
 * per call rather than closed over because the maps change as their queries resolve.
 */
export const filterWorkOrders = (workOrders, query, context) =>
  filterBySearchText(workOrders, query, (row) => getWorkOrderSearchText(row, context));
